import { computed, ref } from 'vue';
import WoowCrmAPI from 'dashboard/api/integrations/woowCrm';

// Shared across every consumer so the header chip and the ticket/lead dialogs
// read the same resolved customer instead of each fetching it. Keyed by
// conversation id — switching conversations paints from cache immediately and
// revalidates in the background.
const summaries = ref({});
const loading = ref({});

export function useWoowCrm(conversationId) {
  const id = computed(() =>
    typeof conversationId === 'function'
      ? conversationId()
      : conversationId?.value
  );

  const summary = computed(() => summaries.value[id.value] ?? null);
  const isLoading = computed(() => Boolean(loading.value[id.value]));
  const isUnavailable = computed(() => Boolean(summary.value?.unavailable));
  const linkedCustomer = computed(() =>
    summary.value?.linked ? summary.value.user : null
  );

  const store = (conversation, data) => {
    summaries.value = { ...summaries.value, [conversation]: data };
  };

  const fetchSummary = async () => {
    const conversation = id.value;
    if (!conversation) return null;

    loading.value = { ...loading.value, [conversation]: true };
    try {
      const { data } = await WoowCrmAPI.getContactSummary(conversation);
      store(conversation, data);
      return data;
    } catch (error) {
      // The CRM bridge is a panel beside the conversation, not a precondition
      // for reading it: an agent WOOW has not provisioned, or a bridge that is
      // down, must still leave the thread openable. Recording the failure as a
      // state the chip can render also stops this rejecting into nobody's
      // catch on every conversation the agent opens.
      store(conversation, {
        unavailable: true,
        error: error?.response?.data?.error || '',
      });
      return null;
    } finally {
      loading.value = { ...loading.value, [conversation]: false };
    }
  };

  const linkCustomer = async userId => {
    const { data } = await WoowCrmAPI.linkCustomer(id.value, userId);
    store(id.value, data);
    return data;
  };

  const unlinkCustomer = async () => {
    const { data } = await WoowCrmAPI.unlinkCustomer(id.value);
    store(id.value, data);
    return data;
  };

  return {
    summary,
    linkedCustomer,
    isLoading,
    isUnavailable,
    fetchSummary,
    linkCustomer,
    unlinkCustomer,
  };
}

// A customer row can carry only a phone number — WOOW accounts created from a
// chat or a phone order often have no name or email — so fall back rather than
// rendering an empty chip.
export function customerLabel(user) {
  if (!user) return '';
  return user.name || user.phone_number || user.user_id || `#${user.id}`;
}
